import { FEATURE_STATUS_COMPLETED } from "config";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import { getProjectFeatureStatus, mapProjectFeatureStatusesToFeatureStatus } from "utils";
import { resolveRunAiHubPipelineVisibility } from "utils/runAiHubPipelineVisibility";

function getFeatureStatus(project: ProjectState | null | undefined, feature: string) {
  const mapped = mapProjectFeatureStatusesToFeatureStatus(
    project?.featureStatuses,
    String(project?.projectId ?? "")
  );
  return getProjectFeatureStatus(mapped, feature)?.status;
}

/** Script breakdown must have completed when the Run AI hub exposes that pipeline. */
export function isScriptBreakdownCompleteForScheduleOrBudget(
  project: ProjectState | null | undefined
): boolean {
  const status = getFeatureStatus(project, "script_breakdown");
  if (status === FEATURE_STATUS_COMPLETED) {
    return true;
  }

  const hub = resolveRunAiHubPipelineVisibility(project ?? null);
  const hasScheduleOrBudget =
    Boolean(project?.features?.hasSchedule) ||
    Boolean(project?.features?.hasBudget) ||
    hub.hasSchedule ||
    hub.hasBudget;

  if (!hasScheduleOrBudget && !hub.hasScriptBreakdown) {
    return true;
  }

  return false;
}

/** Character breakdown must have completed when the Run AI hub exposes that pipeline. */
export function isCharacterBreakdownCompleteForScheduleOrBudget(
  project: ProjectState | null | undefined
): boolean {
  const status = getFeatureStatus(project, "character_breakdown");
  if (status === FEATURE_STATUS_COMPLETED) {
    return true;
  }

  const hub = resolveRunAiHubPipelineVisibility(project ?? null);
  if (!hub.hasCharacterBreakdown) {
    return true;
  }

  return false;
}

export function areScheduleBudgetRunPrerequisitesMet(
  project: ProjectState | null | undefined
): boolean {
  return (
    isScriptBreakdownCompleteForScheduleOrBudget(project) &&
    isCharacterBreakdownCompleteForScheduleOrBudget(project)
  );
}

export function getScheduleBudgetRunPrerequisiteMessage(
  project: ProjectState | null | undefined
): string | undefined {
  if (!isScriptBreakdownCompleteForScheduleOrBudget(project)) {
    return "Finish script breakdown before running schedule or budget AI.";
  }
  if (!isCharacterBreakdownCompleteForScheduleOrBudget(project)) {
    return "Finish character breakdown before running schedule or budget AI.";
  }
  return undefined;
}
